/* generated by AI - see readme for details*/
import type { LayoutMode, LayoutPreference } from 'pane-registry'

const LAYOUT_STORAGE_KEY = 'mashlib-layout'
const MOBILE_BREAKPOINT_PX = 768

const getStoredLayoutPreference = (): LayoutPreference => {
  const storedLayout = localStorage.getItem(LAYOUT_STORAGE_KEY)

  if (storedLayout === 'mobile' || storedLayout === 'desktop' || storedLayout === 'auto') {
    return storedLayout
  }

  return 'auto'
}

const resolveAutomaticLayout = (): LayoutMode => {
  return window.innerWidth <= MOBILE_BREAKPOINT_PX ? 'mobile' : 'desktop'
}

const applyLayoutAttributes = (layout: LayoutMode, preference: LayoutPreference) => {
  const root = document.documentElement
  const inputMode = window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'pointer'

  root.setAttribute('data-layout', layout)
  root.setAttribute('data-layout-preference', preference)
  root.setAttribute('data-input-mode', inputMode)
  root.style.setProperty('--app-height', `${window.innerHeight}px`)

  window.dispatchEvent(new CustomEvent('mashlib:layoutchange', {
    detail: {
      inputMode,
      layout,
      preference,
      viewport: {
        height: window.innerHeight,
        width: window.innerWidth
      }
    }
  }))
}

const updateLayout = (preference: LayoutPreference = getStoredLayoutPreference()): LayoutMode => {
  const layout = preference === 'auto' ? resolveAutomaticLayout() : preference
  applyLayoutAttributes(layout, preference)
  return layout
}

const setLayoutPreference = (preference: LayoutPreference): LayoutMode => {
  if (preference === 'auto') {
    localStorage.removeItem(LAYOUT_STORAGE_KEY)
  } else {
    localStorage.setItem(LAYOUT_STORAGE_KEY, preference)
  }

  return updateLayout(preference)
}

const getLayoutPreference = (): LayoutPreference => {
  return getStoredLayoutPreference()
}

const getLayoutMode = (): LayoutMode => {
  return (document.documentElement.getAttribute('data-layout') as LayoutMode) || resolveAutomaticLayout()
}

const initializeLayout = () => {
  let resizeFrame = 0
  const syncLayout = () => {
    updateLayout()
  }

  const onResize = () => {
    if (resizeFrame) {
      window.cancelAnimationFrame(resizeFrame)
    }

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = 0
      syncLayout()
    })
  }

  syncLayout()
  window.addEventListener('resize', onResize)

  window.matchMedia('(pointer: coarse)').addEventListener('change', syncLayout)
  window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).addEventListener('change', syncLayout)
}

const getInputMode = (): 'touch' | 'pointer' => {
  return window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'pointer'
}

const getViewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight
})

export const layout = {
  get: getLayoutMode,
  getInputMode,
  getPreference: getLayoutPreference,
  getViewport,
  init: initializeLayout,
  set: setLayoutPreference
}
